import { useId } from 'react';
import { useIntl } from 'react-intl';
import {
  getMatrixAnswerSelections,
  getMatrixItemKey,
} from './formularioGlobalMatrixAnswers';

const FormularioGlobalMatrixInput = ({
  disabled,
  error,
  onChange,
  question,
  value,
}) => {
  const intl = useIntl();
  const inputId = useId();
  const selections = getMatrixAnswerSelections(question, value);
  const multiple = !!question.permitirMultiplaSelecaoPorLinha;
  const descriptionId = `${inputId}-description`;
  const errorId = `${inputId}-error`;

  return (
    <fieldset
      aria-describedby={`${descriptionId}${error ? ` ${errorId}` : ''}`}
      className="tw:min-w-0"
      disabled={disabled}
    >
      <legend className="tw:sr-only">{question.titulo}</legend>
      <p className="tw:mb-3 tw:text-sm tw:text-slate-500" id={descriptionId}>
        {intl.formatMessage({
          id: multiple
            ? 'labelMultiplasSelecoesPorLinha'
            : 'labelUmaSelecaoPorLinha',
        })}
      </p>
      <div className="tw:max-w-full tw:overflow-x-auto tw:rounded-lg tw:border tw:border-slate-200">
        <table className="tw:w-full tw:border-collapse tw:text-sm">
          <thead className="tw:bg-white">
            <tr>
              <th className="tw:min-w-40 tw:p-3 tw:text-left" scope="col">
                <span className="tw:sr-only">
                  {intl.formatMessage({ id: 'labelMatrizLinhas' })}
                </span>
              </th>
              {(question.colunas || []).map((column, columnIndex) => (
                <th
                  className="tw:min-w-24 tw:p-3 tw:text-center tw:font-semibold tw:wrap-break-word"
                  key={getMatrixItemKey(column, columnIndex)}
                  scope="col"
                >
                  {column.texto}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {(question.linhas || []).map((row, rowIndex) => {
              const linhaKey = getMatrixItemKey(row, rowIndex);
              const missing =
                !!error &&
                question.obrigatoria &&
                !selections.some(
                  (selection) => selection.linhaKey === linhaKey
                );

              return (
                <tr className="tw:border-t tw:border-slate-200" key={linhaKey}>
                  <th
                    className={`tw:p-3 tw:text-left tw:font-medium tw:wrap-break-word ${
                      missing ? 'tw:text-red-600' : ''
                    }`}
                    scope="row"
                  >
                    {row.texto}
                  </th>
                  {(question.colunas || []).map((column, columnIndex) => {
                    const colunaKey = getMatrixItemKey(column, columnIndex);
                    const checked = selections.some(
                      (selection) =>
                        selection.linhaKey === linhaKey &&
                        selection.colunaKey === colunaKey
                    );

                    return (
                      <td className="tw:p-2 tw:text-center" key={colunaKey}>
                        <label className="tw:flex tw:min-h-10 tw:cursor-pointer tw:items-center tw:justify-center">
                          <span className="tw:sr-only">
                            {row.texto}: {column.texto}
                          </span>
                          <input
                            aria-invalid={missing || undefined}
                            checked={checked}
                            className="tw:h-4 tw:w-4 tw:accent-primary tw:focus-visible:outline-2 tw:focus-visible:outline-offset-2 tw:focus-visible:outline-primary tw:disabled:cursor-not-allowed"
                            disabled={disabled}
                            name={`${inputId}-${linhaKey}`}
                            onChange={(event) => {
                              const nextSelections = selections.filter(
                                (selection) =>
                                  selection.linhaKey !== linhaKey ||
                                  (multiple &&
                                    selection.colunaKey !== colunaKey)
                              );
                              if (event.target.checked) {
                                nextSelections.push({ linhaKey, colunaKey });
                              }
                              onChange(nextSelections);
                            }}
                            type={multiple ? 'checkbox' : 'radio'}
                            value={colunaKey}
                          />
                        </label>
                      </td>
                    );
                  })}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      {error && (
        <p
          className="tw:mb-0 tw:mt-2 tw:text-sm tw:text-red-600"
          id={errorId}
          role="alert"
        >
          {error}
        </p>
      )}
    </fieldset>
  );
};

export default FormularioGlobalMatrixInput;
